import React, { useState, useEffect, useContext, useRef, useCallback} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { VegaForm, VegaFlex, VegaModal,VegaButtonLink, VegaButton, VegaInput, VegaTextarea, VegaInputSelect, VegaAccordion} from "@heartlandone/vega-react";
import { HubContext } from 'Redux/Context/context';
import { FORM_NAME } from 'Chore/Constants';
import { openFormAction, setUserInfoAction } from 'Redux/Actions/actions';
import RegisterActivityListItem from 'Components/ActivityListItem/RegisterActivityListItem';
import Guest from 'Components/Register/Guest';
import { VegaNotify } from "@heartlandone/vega";
import {_fetch} from 'Chore/Request';
import {fetchWithLoader} from 'Chore/loaderRequest';
import {useNavigate, useLocation} from 'react-router-dom';

import { PATH } from 'Chore/Apis';
const { POST_ACTIVITY } = PATH;
const { GET_ACTIVITY } = PATH;
const { GET_BASIC_INFO, GET_USER_INFO } = PATH;

function RegisterForm({ name = '', activity, isEdit }) {
  const { formDispatch, userInfoState : { userInfo }, userInfoDispatch } = useContext(HubContext);
  const [isOpen, setIsOpen] = useState(false);
  const [guests, setGuests] = useState([]);
  const [locations, setLocations] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [currentActivity, setActivity] =  useState(activity);
  const vegaForm = useRef();
  const [form, setForm] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBasicInfoLoaded, setIsBasicInfoLoaded] = useState(false);
  const [isSelfExpand, setIsSelfExpand] = useState(true);
  const [guestExpandStates, setGuestExpandStates] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '..';


  const addComponent = () => {
    if(guests.length < currentActivity?.registerSetting?.maxInvitedGuests){
      const id = uuidv4();
      setGuests([...guests, { id }]);
      setGuestExpandStates(prev => ({ ...prev, [id]: true }));
    }
  }

  const removeComponent = (idToRemove) => {
    setGuests(prev => prev.filter(component => component.id !== idToRemove));
    setGuestExpandStates(prev => {
      const newState = { ...prev };
      delete newState[idToRemove];
      return newState;
    });
  };
  
  const setCurrentRegister = useCallback(async () => {
    const res = await fetchWithLoader({
      api: `${GET_ACTIVITY}/${activity?.id}/register`,
    });
    if (res.code === 2200) {
      const register = res.payload;
      const staff = { ...userInfo, ...register?.staff };
      vegaForm.current.setValue(staff);

      const guestList = register?.guests || [];
      const guestResults = guestList.map((obj, index) => ({
        ...obj,
        id: index + 1,
      }));
      setGuests(guestResults);
    }
  }, [activity?.id, userInfo]); 

  const setCurrentActivity = useCallback(async () => {
    const res = await fetchWithLoader({
      api: `${GET_ACTIVITY}/${activity.id}`,
    });
    if (res.code === 2200) {
      setActivity(res.payload);
    }
  }, [activity?.id]); 


  const setBasicInfo = useCallback(async () => {
    const res = await fetchWithLoader({
      api: `${GET_BASIC_INFO}`,
    });

    if (res.code === 2200) {
      const locations = new Map(
        res.payload.locations.map(p => [p.id, p.name])
      );

      const projectMap = new Map(
        res.payload.projects.map(p => [p.id, p.name])
      );

      const allocations = userInfo?.userAllocations?.map(a => ({
        id: a.projectId,
        displayName: projectMap.get(a.projectId) || 'Unknown Project',
      }));

      setLocations(locations);
      setAllocations(allocations);
    }
  }, [userInfo]);

  useEffect(() => {
    setIsOpen(name === FORM_NAME['REGISTER_FORM']);
  },[name]);

  const _closeModal = () => {
    formDispatch(openFormAction({ name: '' }));
  }

  useEffect(() => {
    const loadData = async () => {
      await setBasicInfo();
      setIsBasicInfoLoaded(true);
      setCurrentActivity();
    };
    loadData();
  }, [setBasicInfo, setCurrentActivity]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchLatestUser = async () => {
      try {
        const res = await fetchWithLoader({ api: GET_USER_INFO });
        if (res && res.code === 2200) {
          const latestUser = res.payload;
          userInfoDispatch(setUserInfoAction({ userInfo: { ...latestUser } }));
        }
      } catch (err) {
        VegaNotify.open({
          type: "error",
          message: 'The user information could not be fetched.',
        });
      }
    };

    fetchLatestUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (isBasicInfoLoaded) {
      if (isEdit) {
        setCurrentRegister();
      } else {
        if (userInfo) {
          vegaForm.current.setValue({
          ...userInfo,
          location: locations.get(userInfo?.locationId) || '',
          allocationId: userInfo?.userAllocations?.length > 0 ? userInfo?.userAllocations[0]?.projectId : ''
        });
      }
      }
    }
  }, [isBasicInfoLoaded, isEdit, setCurrentRegister, userInfo, locations]);

  const _saveRegister = () => {
    setIsSubmitting(true);
    vegaForm.current.valid().then((formRes) => {
      if (formRes.isValid) {
        vegaForm.current.getValue().then(data => {
          setForm({ ...form, ...data });
          const guestsArray = [];

          Object.keys(data)
            .filter(key => key.startsWith('guest'))
            .reduce((result, key) => {
              result[key] = data[key];
              guestsArray.push(result[key]);
              return result;
            }, {});
            
          const formData = {
            staff: {
              name: data?.name,
              phone: data?.phone,
              email: data?.email,
              locationId: userInfo?.locationId,
              allocationId: data?.allocationId,
              nativeName: data?.nativeName,
              note: data?.note
            },
            guests: guestsArray
          };

          _fetch({
            method: isEdit ? "PUT" : 'POST',
            data: formData,
            api: `${POST_ACTIVITY}/${currentActivity?.id}/register`,
          }).then(res => {
            if (res.code === 2200) {
              const message = isEdit? 'The activity has been saved.':'The activity has been registered.';
              VegaNotify.open({
                type: 'success',
                message: message
              });
              _closeModal();
              navigate(from);
            } else {
              VegaNotify.open({
                type: 'error',
                message: res.message
              });
            }
          }).finally(() => {
            setIsSubmitting(false);
          });
        });
      } else {
        setIsSubmitting(false);
      }
    });
  };

  return (
    <VegaModal open={isOpen}  modalTitle={isEdit? 'Edit Register' : 'Register Now'} size="800" onVegaClose={_closeModal} backdrop='static'>
      <div id="loader-container" style={{ maxHeight: 'calc(100vh - 200px)', overflow:'auto', marginLeft: '-24px', marginRight: '-24px', marginBottom: '-24px' }}>
        <div style={{maxHeight:'calc(100% - 80px)', overflow:'auto', paddingLeft: '24px', paddingRight: '24px'}}>
          <VegaFlex direction={'col'} gap={'size-24'} padding="size-24">
            <RegisterActivityListItem activity={currentActivity} isShowActivityState={false}/>

            <VegaForm data-vega-form={`form`} ref={vegaForm}>
              <VegaFlex direction={'col'} gap={'size-24'}>
                <VegaAccordion
                  drawsBackground={false}
                  expandIconAlign={`left`}
                  expand={isSelfExpand}
                  showExpandIcon={currentActivity?.registerSetting?.isAllowInviteGuests}
                  onVegaExpand={() => setIsSelfExpand(prev => !prev)}
                  iconPosition="center"
                >
                  <div slot="title">My Registration</div>
                  <div slot="content">
                    <div className={`v-font-p2-short v-text-primary`}>
                      <VegaFlex direction="col" gap="size-24">
                        <VegaFlex gap="size-24">
                          <VegaInput label="Name" size="small" disabled={true} data-vega-form={"name"} required={true}/>   
                          <VegaInput label="Native Name" size="small" disabled={true} data-vega-form={"nativeName"}/>
                        </VegaFlex>
                        <VegaFlex gap="size-24">
                          <VegaInput label="Phone Number" size="small" disabled={true} data-vega-form={"phone"} required={true}/>                    
                          <VegaInput label="Email" size="small" disabled={true} type="email" required={true} data-vega-form={"email"}/>  
                        </VegaFlex>
                        <VegaFlex gap="size-24">
                          <div className='v-flex-1'><VegaInput label="Location" size="small" disabled={true} required={true} data-vega-form={"location"}/></div>
                          <div id="allocationId" className='v-flex-1'>
                            <VegaInputSelect
                              label='Allocation'
                              vegaDropdownProps={{
                                searchable: false,
                                positionRelativeTo: '#allocationId'
                              }}
                              source={allocations}
                              required={true}
                              data-vega-form={"allocationId"}
                              size={'small'}
                            />
                          </div>
                        </VegaFlex>
                        <VegaFlex >
                          <VegaTextarea label="Note" size="small" maxLength={255} data-vega-form={"note"} />
                        </VegaFlex>
                      </VegaFlex>
                    </div>
                  </div>
                </VegaAccordion>
              
                {guests.map((guest, idx) => (
                  <Guest
                    key={guest.id}
                    model={guest}
                    index={idx + 1}
                    registerSetting={currentActivity.registerSetting}
                    expand={guestExpandStates[guest.id]}
                    toggleExpand={() =>
                      setGuestExpandStates(prev => ({
                        ...prev,
                        [guest.id]: !prev[guest.id]
                      }))
                    }
                    removeComponent={() => removeComponent(guest.id)}
                  />
                ))}
              </VegaFlex>
            </VegaForm>

            <VegaFlex justifyContent='start'>
              { currentActivity?.registerSetting?.isAllowInviteGuests ? <div style={{marginBottom: '12px'}}><VegaButtonLink label="Add Guest" onVegaClick={() => addComponent()  }  icon="far fa-plus" disabled={ guests.length >= currentActivity?.registerSetting?.maxInvitedGuests } /></div>: <></> }
             </VegaFlex>
          </VegaFlex>
        </div>
      
        <div style={{position:'sticky', bottom:'0', right: '0', left: '0', background: 'white', zIndex: '99999' }}>
          <VegaFlex justifyContent='end' alignItem={'center'} className='p-t-20 p-l-32 p-r-32 p-b-20 shadow-top border-none'>
            <VegaFlex gap={'size-12'}>
              <VegaButton variant="secondary" onVegaClick={_closeModal}>Cancel</VegaButton>
              <VegaButton  label = {isEdit? "Save" : "Register"} loading={isSubmitting} onVegaClick={_saveRegister} />
            </VegaFlex>
          </VegaFlex>
        </div>
      </div>
    </VegaModal>
  );
}

export default RegisterForm;

